import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';

interface Subscription {
  id: string;
  userId: string;
  companyName: string;
  tier: 'basic' | 'premium' | 'enterprise';
  monthlyAmount: number;
  status: 'active' | 'cancelled' | 'past_due';
  features: string[];
  startDate: Date;
  nextBilling: Date;
  jobPostingsUsed: number;
  jobPostingsLimit: number;
}

interface SubscriptionSummary {
  totalSubscriptions: number;
  activeSubscriptions: number;
  monthlyRecurringRevenue: number;
  tierDistribution: {
    basic: number;
    premium: number;
    enterprise: number;
  };
  totalJobPostingsUsed: number;
}

const SubscriptionManagement: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  useEffect(() => {
    fetchSubscriptions();
  }, [selectedTier, selectedStatus]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getSubscriptions(
        1, 50,
        selectedStatus !== 'all' ? selectedStatus : undefined,
        selectedTier !== 'all' ? selectedTier : undefined
      );
      
      if (response.success && response.data) {
        // Transform backend data to frontend format if needed
        const backendSubscriptions = response.data.subscriptions || [];
        const transformedSubscriptions: Subscription[] = backendSubscriptions.map((sub: any) => ({
          id: sub.id,
          userId: sub.userId,
          companyName: sub.companyName || 'Unknown Company',
          tier: sub.tier || 'basic',
          monthlyAmount: sub.monthlyAmount || 0,
          status: sub.status || 'active',
          features: sub.features || [],
          startDate: sub.startDate ? new Date(sub.startDate.seconds * 1000) : new Date(),
          nextBilling: sub.nextBilling ? new Date(sub.nextBilling.seconds * 1000) : new Date(),
          jobPostingsUsed: sub.jobPostingsUsed || 0,
          jobPostingsLimit: sub.jobPostingsLimit || 0
        }));

        setSubscriptions(transformedSubscriptions);
        setSummary(response.data.summary || null);
        
        // Show a message if the feature isn't fully implemented
        if (response.message && transformedSubscriptions.length === 0) {
          toast(response.message);
        }
      } else {
        throw new Error('Failed to load subscription data');
      }
    } catch (error: any) {
      console.error('Failed to fetch subscriptions:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (subscriptionId: string, newStatus: string) => {
    try {
      if (newStatus === 'cancelled') {
        const response = await adminApiService.cancelSubscription(subscriptionId, 'Admin action');
        if (response.success) {
          toast.success('Subscription cancelled successfully');
          fetchSubscriptions(); // Refresh data
        } else {
          throw new Error(response.message || 'Failed to cancel subscription');
        }
      } else {
        // For other status changes, use the update method
        const response = await adminApiService.updateSubscription(subscriptionId, { status: newStatus });
        if (response.success) {
          toast.success(`Subscription ${newStatus} successfully`);
          fetchSubscriptions(); // Refresh data
        } else {
          throw new Error(response.message || 'Failed to update subscription status');
        }
      }
    } catch (error: any) {
      console.error('Update subscription status error:', error);
      toast.error('Failed to update subscription status: ' + (error.message || 'Unknown error'));
    }
  };

  const getTierBadge = (tier: string) => {
    const tierConfig = {
      basic: { 
        color: 'bg-gray-100 text-gray-800', 
        label: 'Basic',
        monthlyPrice: '$99'
      },
      premium: { 
        color: 'bg-blue-100 text-blue-800', 
        label: 'Premium',
        monthlyPrice: '$299'
      },
      enterprise: { 
        color: 'bg-purple-100 text-purple-800', 
        label: 'Enterprise',
        monthlyPrice: '$799'
      }
    };
    
    const config = tierConfig[tier as keyof typeof tierConfig];
    if (!config) return null;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
      past_due: { color: 'bg-yellow-100 text-yellow-800', label: 'Past Due' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredSubscriptions = subscriptions.filter(subscription => {
    const tierMatch = selectedTier === 'all' || subscription.tier === selectedTier;
    const statusMatch = selectedStatus === 'all' || subscription.status === selectedStatus;
    return tierMatch && statusMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Subscription Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor and manage employer subscriptions and revenue
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Monthly Recurring Revenue</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(summary.monthlyRecurringRevenue)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Subscriptions</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.activeSubscriptions}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BuildingOfficeIcon className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Subscriptions</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.totalSubscriptions}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BriefcaseIcon className="h-6 w-6 text-orange-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Job Postings Used</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.totalJobPostingsUsed}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tier Distribution */}
      {summary && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Subscription Tier Distribution</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{summary.tierDistribution.basic}</div>
                <div className="text-sm text-gray-500">Basic ($99/month)</div>
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gray-600 h-2 rounded-full" 
                      style={{ width: `${(summary.tierDistribution.basic / summary.totalSubscriptions) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{summary.tierDistribution.premium}</div>
                <div className="text-sm text-gray-500">Premium ($299/month)</div>
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${(summary.tierDistribution.premium / summary.totalSubscriptions) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{summary.tierDistribution.enterprise}</div>
                <div className="text-sm text-gray-500">Enterprise ($799/month)</div>
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: `${(summary.tierDistribution.enterprise / summary.totalSubscriptions) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="tier" className="block text-sm font-medium text-gray-700">
                Tier:
              </label>
              <select
                id="tier"
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Tiers</option>
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status:
              </label>
              <select
                id="status"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="past_due">Past Due</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Subscriptions List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredSubscriptions.map((subscription) => (
            <li key={subscription.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {subscription.companyName}
                        </p>
                        {getTierBadge(subscription.tier)}
                        {getStatusBadge(subscription.status)}
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-500 space-x-4 mb-2">
                        <div className="flex items-center">
                          <CurrencyDollarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          {formatCurrency(subscription.monthlyAmount)}/month
                        </div>
                        <div className="flex items-center">
                          <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          Next billing: {new Date(subscription.nextBilling).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <BriefcaseIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          {subscription.jobPostingsUsed}/{subscription.jobPostingsLimit} job postings
                        </div>
                      </div>

                      {/* Usage Bar */}
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              (subscription.jobPostingsUsed / subscription.jobPostingsLimit) > 0.8 
                                ? 'bg-red-500' 
                                : (subscription.jobPostingsUsed / subscription.jobPostingsLimit) > 0.6 
                                  ? 'bg-yellow-500' 
                                  : 'bg-green-500'
                            }`}
                            style={{ 
                              width: `${Math.min((subscription.jobPostingsUsed / subscription.jobPostingsLimit) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {Math.round((subscription.jobPostingsUsed / subscription.jobPostingsLimit) * 100)}% used
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-1">
                        {subscription.features.slice(0, 3).map((feature) => (
                          <span
                            key={feature}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {feature}
                          </span>
                        ))}
                        {subscription.features.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{subscription.features.length - 3} more features
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-2 flex-shrink-0 flex space-x-2">
                    {subscription.status === 'active' && (
                      <button
                        onClick={() => handleStatusChange(subscription.id, 'cancelled')}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Cancel
                      </button>
                    )}
                    {subscription.status === 'past_due' && (
                      <button
                        onClick={() => handleStatusChange(subscription.id, 'active')}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        Reactivate
                      </button>
                    )}
                    <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {filteredSubscriptions.length === 0 && (
        <div className="text-center py-12">
          <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No subscriptions match the current filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;